import { buildApiUrl, API_CONFIG } from '../config/apiConfig';

/**
 * Servicio de autenticación para el backend de usuarios
 * Maneja registro, login, gestión de perfil y tokens
 * Incluye renovación automática de tokens expirados
 */

class AuthService {
  constructor() {
    this.token = localStorage.getItem('authToken');
    this._isRefreshing = false;
    this._refreshPromise = null;
  }

  // Configurar headers para las requests
  getHeaders(includeAuth = false) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (includeAuth && this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    return headers;
  }

  // Intentar refrescar el token automáticamente
  async _tryRefreshToken() {
    // Evitar múltiples refreshes simultáneos
    if (this._isRefreshing) {
      return this._refreshPromise;
    }

    this._isRefreshing = true;
    this._refreshPromise = (async () => {
      try {
        if (!this.token) {
          return false;
        }

        const url = `${API_CONFIG.USER_API.BASE_URL}/api/auth/refresh-token`;
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          }
        });

        if (!response.ok) {
          return false;
        }

        const data = await response.json();
        
        if (data.success && data.data.token) {
          this.token = data.data.token;
          localStorage.setItem('authToken', this.token);
          
          if (data.data.user) {
            localStorage.setItem('user', JSON.stringify(data.data.user));
          }

          // Notificar al AuthContext del nuevo token
          window.dispatchEvent(new CustomEvent('auth:token-refreshed', {
            detail: { token: this.token, user: data.data.user }
          }));
          
          
          return true;
        }

        return false;
      } catch (error) {
        console.error('Token refresh failed:', error);
        return false;
      } finally {
        this._isRefreshing = false;
        this._refreshPromise = null;
      }
    })();

    return this._refreshPromise;
  }

  // Manejar respuestas de la API - con auto-refresh en 401
  async handleResponse(response, retryFn = null) {
    if (response.status === 401 && retryFn) {
      // Intentar refrescar el token
      const refreshed = await this._tryRefreshToken();
      
      if (refreshed) {
        // Reintentar la request original con el nuevo token
        return await retryFn();
      }
      
      // No se pudo refrescar: notificar al app para redirigir a login
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
    }

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  }

  // Registro de usuario
  async register(userData) {
    try {
      const url = buildApiUrl('USER_API', 'REGISTER');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(userData),
      });

      const data = await this.handleResponse(response);
      
      if (data.success && data.data.token) {
        this.token = data.data.token;
        localStorage.setItem('authToken', this.token);
        localStorage.setItem('user', JSON.stringify(data.data.user));
      }

      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Login de usuario
  async login(email, password) {
    try {
      const url = buildApiUrl('USER_API', 'LOGIN');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ email, password }),
      });

      const data = await this.handleResponse(response);
      
      if (data.success && data.data.token) {
        this.token = data.data.token;
        localStorage.setItem('authToken', this.token);
        localStorage.setItem('user', JSON.stringify(data.data.user));
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Login con Google (ID token)
  async loginWithGoogle(idToken, selectedPlan) {
    try {
      const url = buildApiUrl('USER_API', 'GOOGLE');
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ idToken, ...(selectedPlan && { selectedPlan }) }),
      });
      const data = await this.handleResponse(response);
      if (data.success && data.data.token) {
        this.token = data.data.token;
        localStorage.setItem('authToken', this.token);
        localStorage.setItem('user', JSON.stringify(data.data.user));
      }
      return data;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }

  // Obtener perfil del usuario
  async getProfile() {
    try {
      if (!this.token) {
        throw new Error('No authentication token found');
      }

      const url = buildApiUrl('USER_API', 'PROFILE');
      
      const doFetch = async () => {
        const resp = await fetch(url, {
          method: 'GET',
          headers: this.getHeaders(true),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || data.message || 'Request failed');
        return data;
      };

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(true),
      });

      return await this.handleResponse(response, doFetch);
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }

  // Actualizar perfil del usuario
  async updateProfile(profileData) {
    try {
      if (!this.token) {
        throw new Error('No authentication token found');
      }

      const url = buildApiUrl('USER_API', 'UPDATE_PROFILE');
      
      const doFetch = async () => {
        const resp = await fetch(url, {
          method: 'PUT',
          headers: this.getHeaders(true),
          body: JSON.stringify(profileData),
        });
        const d = await resp.json();
        if (!resp.ok) throw new Error(d.error || d.message || 'Request failed');
        return d;
      };

      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getHeaders(true),
        body: JSON.stringify(profileData),
      });

      const data = await this.handleResponse(response, doFetch);
      
      if (data.success && data.data.user) {
        localStorage.setItem('user', JSON.stringify(data.data.user));
      }

      return data;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  // Cambiar contraseña
  async changePassword(currentPassword, newPassword) {
    try {
      if (!this.token) {
        throw new Error('No authentication token found');
      }

      const url = buildApiUrl('USER_API', 'CHANGE_PASSWORD');
      
      const doFetch = async () => {
        const resp = await fetch(url, {
          method: 'POST',
          headers: this.getHeaders(true),
          body: JSON.stringify({ currentPassword, newPassword }),
        });
        const d = await resp.json();
        if (!resp.ok) throw new Error(d.error || d.message || 'Request failed');
        return d;
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(true),
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      return await this.handleResponse(response, doFetch);
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  // Verificar si el token es válido (intenta refresh si está expirado)
  async verifyToken() {
    try {
      if (!this.token) {
        return { success: false, error: 'No token found' };
      }

      const url = buildApiUrl('USER_API', 'VERIFY_TOKEN');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(true),
      });

      if (response.status === 401) {
        // Token expirado: intentar refrescar
        
        const refreshed = await this._tryRefreshToken();
        
        if (refreshed) {
          // Reintentar verificación con el nuevo token
          const retryResponse = await fetch(url, {
            method: 'GET',
            headers: this.getHeaders(true),
          });
          const retryData = await retryResponse.json();
          if (retryResponse.ok) {
            return retryData;
          }
        }
        
        // No se pudo refrescar
        this.logout();
        return { success: false, error: 'Token expired and could not be refreshed' };
      }

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Token verification error:', error);
      this.logout();
      return { success: false, error: error.message };
    }
  }

  // Logout
  async logout() {
    try {
      if (this.token) {
        const url = buildApiUrl('USER_API', 'LOGOUT');
        
        await fetch(url, {
          method: 'POST',
          headers: this.getHeaders(true),
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Limpiar datos locales independientemente del resultado del API
      this.token = null;
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  }

  // Alta desde /pricing que vuelve del Checkout sin pagar: calc-api elimina la
  // cuenta si es un alta reciente y vacía. Devuelve true si se eliminó.
  async discardPendingSignup() {
    if (!this.token) return false;
    try {
      const resp = await fetch(buildApiUrl('USER_API', 'PENDING_SIGNUP'), {
        method: 'DELETE',
        headers: this.getHeaders(true),
      });
      if (!resp.ok) return false;
      const data = await resp.json();
      return data?.data?.deleted === true;
    } catch (error) {
      console.error('Discard pending signup error:', error);
      return false;
    }
  }

  // Verificar si el usuario está autenticado
  isAuthenticated() {
    return !!this.token;
  }

  // Obtener usuario actual del localStorage
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Obtener token actual
  getToken() {
    return this.token;
  }

  // Verificar estado del backend
  async healthCheck() {
    try {
      const url = buildApiUrl('USER_API', 'HEALTH');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Health check error:', error);
      return { success: false, error: error.message };
    }
  }
}

// Exportar instancia singleton
export const authService = new AuthService();

// Exportar también la clase para testing
export default AuthService;
